import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {ToastService} from '@sms-ir/ui-components';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  from,
  map,
  of,
  Subject,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import {LayoutService} from '@app/core/services';
import {
  ALL_CONTACTS_GROUP_ID,
  ALL_CONTACTS_LABEL,
  CONTACTS_PAGE_SIZE,
  COMING_SOON_MESSAGE,
  SEARCH_DEBOUNCE_MS,
} from '../constants/contacts.constants';
import {CONTACTS_API} from '../services/contacts-api.port';
import {CONTACTS_GROUPS_API} from '../services/contacts-groups-api.port';
import {EVENT_MEMBERS_API} from '../services/event-members-api.port';
import {showContactsToast} from '../utils/contacts.helpers';
import type {
  Contact,
  ContactGroup,
  ContactGroupId,
  ContactGroupListItem,
  ContactRowAction,
  ContactsTab,
  CreateContactGroupPayload,
  DeleteContactPayload,
} from '../types/contacts.types';

type ContactsMobileView = 'groups' | 'contacts';

@Injectable()
export class ContactsFacade {
  private readonly destroyRef = inject(DestroyRef);
  private readonly contactsApi = inject(CONTACTS_API);
  private readonly groupsApi = inject(CONTACTS_GROUPS_API);
  private readonly eventMembersApi = inject(EVENT_MEMBERS_API);
  private readonly toast = inject(ToastService);
  private readonly layout = inject(LayoutService);

  private readonly groupSearch$ = new Subject<string>();
  private readonly contactSearch$ = new Subject<string>();
  private readonly debouncedGroupSearchQuery = signal('');

  readonly activeTab = signal<ContactsTab>('groups');
  readonly activeGroupId = signal<ContactGroupId>(ALL_CONTACTS_GROUP_ID);
  readonly groups = signal<ContactGroup[]>([]);
  readonly contacts = signal<Contact[]>([]);
  readonly groupsLoading = signal(false);
  readonly contactsLoading = signal(false);
  readonly groupSearchQuery = signal('');
  readonly contactSearchQuery = signal('');
  readonly page = signal(1);
  readonly pageSize = signal(CONTACTS_PAGE_SIZE);
  readonly totalCount = signal(0);

  readonly selectionForm = new FormGroup({
    selectedItems: new FormControl<Contact[]>([], {nonNullable: true}),
  });

  readonly selectedItems = toSignal(this.selectionForm.controls.selectedItems.valueChanges, {
    initialValue: [] as Contact[],
  });
  readonly selectedCount = computed(() => this.selectedItems().length);
  readonly hasSelection = computed(() => this.selectedCount() > 0);
  readonly hasSearch = computed(() => this.contactSearchQuery().trim().length > 0);
  readonly isAllContactsView = computed(() => this.activeGroupId() === ALL_CONTACTS_GROUP_ID);
  readonly canManageActiveGroup = computed(() => !this.isAllContactsView());
  readonly hasActiveGroup = computed(() => this.activeGroupId() != null);

  readonly allContactsCount = computed(() => {
    if (this.isAllContactsView() && this.totalCount() > 0) {
      return this.totalCount();
    }
    return this.groups().reduce((sum, group) => sum + group.contactsCount, 0);
  });

  readonly sidebarGroups = computed((): ContactGroupListItem[] => {
    const query = this.groupSearchQuery().trim().toLowerCase();
    const userItems = this.groups()
      .filter(group => !query || group.name.toLowerCase().includes(query))
      .map(group => ({
        id: group.id,
        name: group.name,
        contactsCount: group.contactsCount,
        isSystem: false,
      }));

    return [
      {
        id: ALL_CONTACTS_GROUP_ID,
        name: ALL_CONTACTS_LABEL,
        contactsCount: this.allContactsCount(),
        isSystem: true,
      },
      ...userItems,
    ];
  });

  readonly showGroupSearchEmpty = computed(() => {
    const query = this.groupSearchQuery().trim();
    if (!query) {
      return false;
    }
    return this.sidebarGroups().every(group => group.isSystem);
  });

  readonly activeGroupName = computed(() => {
    if (this.isAllContactsView()) {
      return ALL_CONTACTS_LABEL;
    }
    return this.groups().find(group => group.id === this.activeGroupId())?.name ?? null;
  });

  readonly showTable = computed(
    () => this.hasActiveGroup() && !this.contactsLoading() && this.totalCount() > 0,
  );
  readonly showEmpty = computed(
    () => this.hasActiveGroup() && !this.contactsLoading() && this.totalCount() === 0,
  );

  readonly mobileView = signal<ContactsMobileView>(this.layout.isMobile() ? 'groups' : 'contacts');
  readonly showMobileGroupList = computed(
    () => this.layout.isMobile() && this.mobileView() === 'groups',
  );
  readonly showMobileContactsDetail = computed(
    () => this.layout.isMobile() && this.mobileView() === 'contacts',
  );

  constructor() {
    this.groupSearch$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(phrase => {
          this.debouncedGroupSearchQuery.set(phrase);
          return this.loadGroups();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.contactSearch$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(() => {
          this.page.set(1);
          this.clearSelection();
          return this.loadContacts();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loadGroups()
      .pipe(
        switchMap(() => this.loadContacts()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTabChange(tab: ContactsTab): void {
    this.activeTab.set(tab);
  }

  onGroupSearch(value: string): void {
    this.groupSearchQuery.set(value);
    this.groupSearch$.next(value.trim());
  }

  onContactSearch(value: string): void {
    this.contactSearchQuery.set(value);
    this.contactSearch$.next(value.trim());
  }

  selectGroup(groupId: string, _groupName?: string): void {
    this.activeGroupId.set(groupId);
    this.page.set(1);
    this.clearSelection();
    if (this.layout.isMobile()) {
      this.mobileView.set('contacts');
    }
    this.reloadContacts();
  }

  onMobileGroupBack(): void {
    this.mobileView.set('groups');
    this.activeGroupId.set(ALL_CONTACTS_GROUP_ID);
    this.clearSelection();
    this.clearSearch();
  }

  onPageChange(nextPage: number): void {
    this.page.set(nextPage);
    this.clearSelection();
    this.reloadContacts();
  }

  clearSelection(): void {
    this.selectionForm.controls.selectedItems.setValue([]);
  }

  clearSearch(): void {
    this.contactSearchQuery.set('');
    this.contactSearch$.next('');
  }

  createGroup(payload: CreateContactGroupPayload): void {
    this.groupsApi
      .createGroup(payload)
      .pipe(
        catchError(() => {
          this.notify('error', 'ایجاد گروه با خطا مواجه شد.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(group => {
        if (!group) return;
        this.notify('success', 'گروه با موفقیت ایجاد شد.');
        this.reloadGroups();
        this.selectGroup(group.id, group.name);
      });
  }

  updateActiveGroupName(name: string): void {
    if (!this.canManageActiveGroup()) return;

    const groupId = this.activeGroupId();
    if (!groupId) return;

    this.groupsApi
      .updateGroup(groupId, name)
      .pipe(
        catchError(() => {
          this.notify('error', 'تغییر نام گروه با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', 'نام گروه با موفقیت تغییر یافت.');
        this.reloadGroups();
      });
  }

  deleteActiveGroup(): void {
    if (!this.canManageActiveGroup()) return;

    const groupId = this.activeGroupId();
    if (!groupId) return;

    this.groupsApi
      .deleteGroup(groupId)
      .pipe(
        catchError(() => {
          this.notify('error', 'حذف گروه با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', 'گروه مورد نظر حذف شد.');
        this.activeGroupId.set(ALL_CONTACTS_GROUP_ID);
        this.clearSelection();
        if (this.layout.isMobile()) {
          this.mobileView.set('groups');
        }
        this.refreshLists();
      });
  }

  deleteContact(payload: DeleteContactPayload): void {
    this.contactsApi
      .deleteContact(payload)
      .pipe(
        catchError(() => {
          this.notify('error', 'حذف مخاطب با خطا مواجه شد.');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', 'مخاطب با موفقیت حذف شد.');
        this.clearSelection();
        this.refreshLists();
      });
  }

  deleteSelectedContacts(deleteGlobally: boolean): void {
    const items = this.selectedItems();
    if (!items.length) return;

    const payloads: DeleteContactPayload[] = items
      .map(contact => {
        const groupId = this.resolveContactGroupId(contact);
        if (!groupId && !deleteGlobally) {
          return null;
        }
        return {
          contactId: contact.id,
          groupId: groupId ?? this.activeGroupId(),
          deleteGlobally,
        };
      })
      .filter((payload): payload is DeleteContactPayload => payload != null);

    from(payloads)
      .pipe(
        concatMap(payload =>
          this.contactsApi.deleteContact(payload).pipe(catchError(() => of(undefined))),
        ),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notify('success', `${items.length.toLocaleString('fa-IR')} مخاطب با موفقیت حذف شد.`);
        this.clearSelection();
        this.refreshLists();
      });
  }

  refreshContacts(): void {
    this.refreshLists();
  }

  transferContacts(contacts: Contact[], destinationGroupIds: string[]): void {
    if (!contacts.length || !destinationGroupIds.length) return;

    const mobiles = contacts
      .map(contact => Number(String(contact.mobile).replace(/\s+/g, '')))
      .filter(mobile => !Number.isNaN(mobile) && mobile > 0);
    if (!mobiles.length) {
      this.notify('error', 'شماره موبایل معتبری برای انتقال یافت نشد.');
      return;
    }

    forkJoin(
      destinationGroupIds.map(groupId =>
        this.contactsApi.bulkPreview(groupId, mobiles).pipe(
          switchMap(result => {
            if (!result) return of(null);
            return this.contactsApi.bulkConfirm(groupId, result.key).pipe(map(() => result));
          }),
        ),
      ),
    )
      .pipe(
        catchError(() => {
          this.notify('error', 'انتقال مخاطب به گروه با خطا مواجه شد.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(results => {
        if (!results) return;
        const successCount = results.filter(result => result != null).length;
        if (!successCount) return;
        const countLabel = contacts.length.toLocaleString('fa-IR');
        const multipleGroups = destinationGroupIds.length > 1;
        this.notify(
          'success',
          contacts.length === 1
            ? multipleGroups
              ? 'مخاطب با موفقیت به گروه‌ها منتقل شد.'
              : 'مخاطب با موفقیت به گروه منتقل شد.'
            : multipleGroups
              ? `${countLabel} مخاطب با موفقیت به گروه‌ها منتقل شد.`
              : `${countLabel} مخاطب با موفقیت به گروه منتقل شد.`,
        );
        this.clearSelection();
        this.refreshLists();
      });
  }

  addContactsToEvent(contacts: Contact[], eventIds: string[]): void {
    if (!contacts.length || !eventIds.length) return;

    const contactIds = contacts.map(contact => contact.id).filter(Boolean);
    if (!contactIds.length) {
      this.notify('error', 'مخاطب معتبری برای افزودن به رویداد یافت نشد.');
      return;
    }

    forkJoin(eventIds.map(eventId => this.eventMembersApi.addMembersByContactIds(eventId, contactIds)))
      .pipe(
        catchError(() => {
          this.notify('error', 'افزودن مخاطب به رویداد با خطا مواجه شد.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(results => {
        if (!results) return;
        const countLabel = contacts.length.toLocaleString('fa-IR');
        const multipleEvents = eventIds.length > 1;
        this.notify(
          'success',
          contacts.length === 1
            ? multipleEvents
              ? 'مخاطب با موفقیت به رویدادها اضافه شد.'
              : 'مخاطب با موفقیت به رویداد اضافه شد.'
            : multipleEvents
              ? `${countLabel} مخاطب با موفقیت به رویدادها اضافه شد.`
              : `${countLabel} مخاطب با موفقیت به رویداد اضافه شد.`,
        );
        this.clearSelection();
        this.refreshLists();
      });
  }

  exportActiveGroup(): void {
    if (!this.canManageActiveGroup()) return;

    const groupId = this.activeGroupId();
    if (!groupId) return;

    window.open(this.contactsApi.getExportUrl(groupId), '_blank');
  }

  handleFilterAction(): void {
    this.notifyStubAction();
  }

  handleRowAction(_contact: Contact, _action: ContactRowAction): void {
    this.notifyStubAction();
  }

  handleBulkAction(action: 'moveToGroup' | 'addToEvent' | 'delete' | 'export'): void {
    if (action === 'delete') return;
    this.notifyStubAction();
  }

  private notifyStubAction(): void {
    showContactsToast(this.toast, 'success', COMING_SOON_MESSAGE);
  }

  private refreshLists(): void {
    forkJoin([this.loadGroups(), this.loadContacts()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private reloadGroups(): void {
    this.loadGroups().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private reloadContacts(): void {
    this.loadContacts().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private loadGroups() {
    this.groupsLoading.set(true);
    return this.groupsApi
      .listGroups({phrase: this.debouncedGroupSearchQuery() || undefined, page: 1, pageSize: 200})
      .pipe(
        catchError(() => of({items: [], total: 0})),
        finalize(() => this.groupsLoading.set(false)),
        tap(result => {
          this.groups.set(result.items);
          const activeId = this.activeGroupId();
          if (!activeId || activeId === ALL_CONTACTS_GROUP_ID) return;

          const stillExists = result.items.some(group => group.id === activeId);
          if (!stillExists) {
            this.activeGroupId.set(ALL_CONTACTS_GROUP_ID);
            this.reloadContacts();
          }
        }),
      );
  }

  private loadContacts() {
    const groupId = this.activeGroupId();
    if (!groupId) {
      this.contacts.set([]);
      this.totalCount.set(0);
      return of({items: [], total: 0});
    }

    this.contactsLoading.set(true);
    return this.contactsApi
      .listContacts({
        groupId,
        groupName: this.isAllContactsView() ? undefined : (this.activeGroupName() ?? undefined),
        phrase: this.contactSearchQuery().trim() || undefined,
        page: this.page(),
        pageSize: this.pageSize(),
      })
      .pipe(
        catchError(() => of({items: [], total: 0})),
        finalize(() => this.contactsLoading.set(false)),
        tap(result => {
          this.contacts.set(result.items);
          this.totalCount.set(result.total);
        }),
      );
  }

  resolveContactGroupId(contact: Contact): string | null {
    if (!this.isAllContactsView()) {
      return this.activeGroupId();
    }

    for (const groupName of contact.groups) {
      const matchedGroup = this.groups().find(group => group.name === groupName);
      if (matchedGroup) {
        return matchedGroup.id;
      }
    }

    return this.groups()[0]?.id ?? null;
  }

  private notify(variant: 'error' | 'success' | 'warning', message: string): void {
    showContactsToast(this.toast, variant, message);
  }
}
